import React, { createContext, useState, useEffect } from 'react';
import { auth, firestore } from './firebase-config'; // Import Firebase auth and firestore
import { doc, getDoc, updateDoc } from 'firebase/firestore'; // Import Firestore functions

// Create a Context for authentication
export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [highscore, setHighscore] = useState(0);
  const [rank, setRank] = useState('User');
  const [loading, setLoading] = useState(true);
  const [blogDarkMode, setBlogDarkMode] = useState(() => {
    const stored = localStorage.getItem('blogDarkMode');
    return stored === null ? true : stored === 'true'; // default: dark
  });

  // Fetch user data and rank from Firestore
  const fetchUserData = async (user) => {
    try {
      const userRef = doc(firestore, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const userInfo = {
          uid: user.uid,
          email: user.email,
          username: userData.username || user.email.split('@')[0],
          role: userData.role || 'User',
        };
        setLoggedInUser(userInfo);
        setRank(userData.rank || 'User');
        localStorage.setItem('user', JSON.stringify(userInfo));
        // Load blog dark mode preference
        const savedDarkMode = userData.blogDarkMode ?? false;
        setBlogDarkMode(savedDarkMode);
        localStorage.setItem('blogDarkMode', savedDarkMode); // Store in localStorage
      } else {
        // If no Firestore data exists, set minimal user info
        const userInfo = {
          uid: user.uid,
          email: user.email,
          username: user.email.split('@')[0],
          role: 'User',
        };
        setLoggedInUser(userInfo);
        setRank('User');
        localStorage.setItem('user', JSON.stringify(userInfo));
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setRank('User');
    } finally {
      setLoading(false);
    }
  };

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        // User is signed in, fetch their data
        fetchUserData(user);
        // Restore highscore from localStorage if it exists
        const storedHighscore = localStorage.getItem('highscore');
        if (storedHighscore) {
          setHighscore(parseInt(storedHighscore, 10));
        }
      } else {
        // No user is signed in
        setLoggedInUser(null);
        setHighscore(0);
        setRank('User');
        localStorage.removeItem('user');
        localStorage.removeItem('highscore');
        setLoading(false);
      }
    });

    return () => unsubscribe(); // Cleanup on unmount
  }, []);

  const loginUser = (userData) => {
    // Ensure uid is included in userData
    const userInfo = {
      uid: userData.uid || userData.email, // Fallback to email if uid is missing
      email: userData.email,
      username: userData.username || userData.email.split('@')[0],
      role: userData.role || 'User',
    };
    setLoggedInUser(userInfo);
    localStorage.setItem('user', JSON.stringify(userInfo));
    setRank(userData.role || 'User'); // Use role as rank for consistency
  };

  const logoutUser = () => {
    setLoggedInUser(null);
    setHighscore(0);
    setRank('User');
    localStorage.removeItem('user');
    localStorage.removeItem('highscore');
  };

  const updateHighscore = (newHighscore) => {
    if (newHighscore > highscore) {
      setHighscore(newHighscore);
      localStorage.setItem('highscore', newHighscore);
    }
  };

  const toggleBlogDarkMode = async () => {
    const newValue = !blogDarkMode;
    setBlogDarkMode(newValue);
    localStorage.setItem('blogDarkMode', newValue);
    if (loggedInUser?.uid) {
      try {
        await updateDoc(doc(firestore, 'users', loggedInUser.uid), { blogDarkMode: newValue });
      } catch (e) {
        console.error('Failed to save dark mode preference:', e);
      }
    }
  };

  return (
    <AuthContext.Provider
      value={{ loggedInUser, loginUser, logoutUser, highscore, rank, updateHighscore, loading, blogDarkMode, toggleBlogDarkMode }}
    >
      {children}
    </AuthContext.Provider>
  );
};